import { PlayerListResponseDto } from "@/lib/api/interfaces/common.interface";
import { usePlayersDelete, usePlayersFindQuery as usePlayersFind, UsePlayersFindParams, usePlayersBulkDelete } from "@/lib/api/requests/players.requests";
import { CsvImportModal } from "@/ui-components/csv-import/CsvImportModal";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import {
  DataView,
  DataViewContent,
  DataViewEmptyState,
  DataViewGridCard,
  DataViewLayout,
  DataViewListCard,
  DataViewLoadingState,
  DataViewLoadMore,
  DataViewToolbar,
  GenericItem,
  GenericItemActions,
  EntityPreviewFrame
} from "@/util-components/data-view";
import { PageHeader, PageLayout, PageTitle, PrimaryActionButton, PrimaryActionDropdown } from "@/util-components/page-layout";
import { Contact, Plus, FileSpreadsheet } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { usePageState } from "@/lib/hooks/use-page-state";
import { AdvancedFilterDef, AdvancedSortDef, AdvancedState, SortDirection } from "@/util-components/data-view/components/advanced-filter-types";
import { Users } from "lucide-react";
import CreatePlayerDialog from "./create-dialog";
import PlayerDetailsDialog from "./details-dialog";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { translatePosition, PLAYER_POSITIONS } from "@/lib/utils/player-utils";
import { usePlayerCsvImportConfig } from "@/page-components/players/import/player-import.config";

const DEFAULT_PAGE_SIZE = 20;

const PlayersWrapper = () => {
  return (
    <PageLayout basePath="/spieler">
      <Players />
    </PageLayout>
  );
};

const Players = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, setSettings, setViewMode } = usePageState('players-page', {
    viewMode: 'grid',
    settings: {
      advancedState: {
        filters: [],
        sorts: [{ key: 'firstName', direction: 'asc' as SortDirection }]
      } as AdvancedState
    }
  });
  const viewMode = state.viewMode || 'grid';
  // Advanced state integration - Always used
  const advancedState = useMemo(() => {
    const s = (state.settings?.advancedState as AdvancedState);
    if (!s) return { filters: [], sorts: [{ key: 'firstName', direction: 'asc' as const }] };
    // Enforce default sort if empty (prevent "stuck" state with disableMultiSort)
    if (!s.sorts || s.sorts.length === 0) {
       return { ...s, sorts: [{ key: 'firstName', direction: 'asc' as const }] };
    }
    return s;
  }, [state.settings?.advancedState]);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('textSearch') || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [allPlayers, setAllPlayers] = useState<PlayerListResponseDto[]>([]);
  const [lastFilterKey, setLastFilterKey] = useState<string>('');
  const [teamOptions, setTeamOptions] = useState<{ label: string; value: string }[]>([]);

  // Dialog states
  const [detailsPopupOpen, setDetailsPopupOpen] = useState<{
    open: boolean;
    user: PlayerListResponseDto | null;
    mode: "details" | "edit";
  }>({
    open: false,
    user: null,
    mode: "details",
  });

  const [createPlayerDialogOpen, setCreatePlayerDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; player: PlayerListResponseDto | null; isBulk?: boolean }>({
    open: false,
    player: null,
    isBulk: false
  });
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { mutate: deletePlayer, isPending: isDeleting } = usePlayersDelete();
  const { mutate: bulkDeletePlayers, isPending: isBulkDeleting } = usePlayersBulkDelete();
  const importConfig = usePlayerCsvImportConfig();

  // Filter params
  const currentFilterKey = useMemo(() => {
    // Only include filters that actually have a value (matching params logic)
    // This prevents list reset when adding a new empty filter row
    const activeFilters = advancedState.filters.filter(f => f.value);
    return `${searchTerm.trim()}-${JSON.stringify(activeFilters)}-${JSON.stringify(advancedState.sorts)}`;
  }, [searchTerm, advancedState]);

  const params: UsePlayersFindParams = useMemo(() => {
    // Base params
    const baseParams: UsePlayersFindParams = {
        textSearch: searchTerm.trim() || undefined,
        page: currentPage,
        limit: DEFAULT_PAGE_SIZE,
    };

    // Advanced overrides
    // Map advanced filters to params
    // Best Practice: Ignore filters that have no value selected (undefined or empty string).
    // Only explicitly send 'null' if it's a valid string value (e.g. for "Keine Position").
    const teamFilter = advancedState.filters.find(f => f.key === 'teamId');
    if (teamFilter && teamFilter.value) {
        baseParams.teamId = teamFilter.value;
    }
    const positionFilter = advancedState.filters.find(f => f.key === 'position');
    if (positionFilter && positionFilter.value) {
        baseParams.position = positionFilter.value;
    }

    // Map advanced sorts
    if (advancedState.sorts.length > 0) {
        const primarySort = advancedState.sorts[0];
        if (primarySort) {
            baseParams.sort = primarySort.key;
            baseParams.order = primarySort.direction;
        }
    }

    return baseParams;
  }, [searchTerm, currentPage, advancedState]);

  // Query
  const { data, isLoading, refetch, isError } = usePlayersFind(params, {
    skipGlobalErrorHandler: true
  });

  // Show toast on error
  useEffect(() => {
    if (isError) {
      toast.error("Fehler beim Laden der Spieler");
    }
  }, [isError]);
  
  // Reset on filter change
  useEffect(() => {
    if (currentFilterKey !== lastFilterKey) {
      setCurrentPage(1);
      setAllPlayers([]);
      setLastFilterKey(currentFilterKey);
    }
  }, [currentFilterKey, lastFilterKey]);

  // Accumulate data
  useEffect(() => {
    if (data?.players?.docs) {
      if (currentPage === 1) {
        setAllPlayers(data.players.docs);
      } else {
        setAllPlayers(prev => {
          const newIds = new Set(data.players.docs.map(d => d._id));
          return [...prev.filter(p => !newIds.has(p._id)), ...data.players.docs];
        });
      }
    }

    if (data?.listEntities?.teams) {
      setTeamOptions(data.listEntities.teams.map(t => ({
        label: t.value,
        value: t._id
      })));
    }
  }, [data, currentPage]);

  // Sync URL params
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (searchTerm) {
      newParams.set('textSearch', searchTerm);
    } else {
      newParams.delete('textSearch');
    }
    
    // TODO: Sync advanced state to URL if needed, but requirements said "persist in page-state" which is localStorage usually.

    setSearchParams(newParams, { replace: true });
  }, [searchTerm, setSearchParams, searchParams]);

  const hasMore = data?.players?.hasNextPage || false;
  const isInitialLoading = isLoading && currentPage === 1;
  const isLoadingMore = isLoading && currentPage > 1;

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearchTerm(text);
  };

  const handleDelete = async () => {
    if (deleteConfirm.isBulk && selectedIds.length > 0) {
      await bulkDeletePlayers({ ids: selectedIds }, {
        onSuccess: async () => {
            toast.success(`${selectedIds.length} Spieler erfolgreich gelöscht`);
            await refetch();
            setCurrentPage(1); 
            setAllPlayers(prev => prev.filter(p => !selectedIds.includes(p._id)));
            setSelectedIds([]);
            setDeleteConfirm({ open: false, player: null, isBulk: false });
        },
        onError: (error) => {
            toast.error("Spieler konnten nicht gelöscht werden", { error });
        }
      });
      return;
    }

    if (!deleteConfirm.player) return;

    await deletePlayer({ id: deleteConfirm.player._id }, {
      onSuccess: async () => {
          toast.success("Spieler erfolgreich gelöscht");
          await refetch();
          setCurrentPage(1); 
          setAllPlayers(prev => prev.filter(p => p._id !== deleteConfirm.player!._id));
          setDeleteConfirm({ open: false, player: null });
      },
      onError: (error) => {
          toast.error("Spieler konnte nicht gelöscht werden", { error });
      }
    });
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setDeleteConfirm({ open: true, player: null, isBulk: true });
    }
  };

  const handleCreateSuccess = async () => {
    await refetch();
    setCurrentPage(1);
  };

  const handleDetailsClose = async (shouldRefetch?: boolean) => {
    setDetailsPopupOpen({ open: false, user: null, mode: "details" });
    if (shouldRefetch) {
      await refetch();
      setCurrentPage(1);
    }
  };

  // Convert to GenericItems
  const convertToGenericItem = useCallback((player: PlayerListResponseDto): GenericItem => {
    const teamName = data?.listEntities?.teams.find(t => t._id === player.belongsToTeamId)?.value || "Kein Team";
    
    const initials = `${player.firstName.substring(0, 1)}${player.lastName.substring(0, 1)}`;
    const imageUrl = player.thumbnailUrl || player.imageUrl;
    
    return {
      id: player._id,
      title: `${player.firstName} ${player.lastName}`,
      description: `${teamName} • ${player.position ? translatePosition(player.position) : "Spieler"}`,
      previewContent: (
        <EntityPreviewFrame backgroundUrl={imageUrl}>
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={`${player.firstName} ${player.lastName}`} 
              className="h-full w-full object-cover object-top" 
            />
          ) : (
            <span className="text-2xl font-bold tracking-tight text-blue-600 uppercase">
              {initials}
            </span>
          )}
        </EntityPreviewFrame>
      ),
      previewContentList: (
        <div className="w-full h-full flex items-center justify-center bg-blue-50">
            {imageUrl ? (
                <img src={imageUrl} alt="" className="w-full h-full object-cover object-top" />
            ) : (
                <span className="text-[10px] font-bold text-blue-600 uppercase">
                    {initials}
                </span>
            )}
        </div>
      ),
      metadata: {
        badgeText: player.jerseyNumber ? `#${player.jerseyNumber}` : undefined,
      }
    };
  }, [data?.listEntities?.teams]);

  const genericItems = useMemo(() => {
    return allPlayers.map(convertToGenericItem);
  }, [allPlayers, convertToGenericItem]);

  const genericActions: GenericItemActions = {
    onItemClick: (id) => {
      const player = allPlayers.find(p => p._id === id);
      if (player) setDetailsPopupOpen({ open: true, user: player, mode: "details" });
    },
    onEdit: (id) => {
      const player = allPlayers.find(m => m._id === id);
      if (player) setDetailsPopupOpen({ open: true, user: player, mode: "edit" });
    },
    onDelete: (id) => {
      const player = allPlayers.find(p => p._id === id);
      if (player) setDeleteConfirm({ open: true, player });
    }
  };



  // --- Advanced Defs ---
  const advancedFilterDefs = useMemo<AdvancedFilterDef[]>(() => {
    return [
       {
         key: 'teamId',
         label: 'Mannschaft',
         type: 'select',
         icon: Users,
         options: teamOptions
       },
       {
         key: 'position',
         label: 'Position',
         type: 'select',
         icon: Contact, // Using Contact icon for position
         options: [
           ...PLAYER_POSITIONS,
           { label: 'Keine Position', value: 'null' }
         ]
       },
       // Example boolean filter if backend supported it
       /*
       {
         key: 'isActive',
         label: 'Aktiv',
         type: 'boolean'
       }
       */
    ];
  }, [teamOptions]);

  const advancedSortDefs = useMemo<AdvancedSortDef[]>(() => [
     { key: 'firstName', label: 'Vorname' },
     { key: 'lastName', label: 'Nachname' },
     { key: 'age', label: 'Alter' }, // Mapped to birthDate in backend
     { key: 'team', label: 'Mannschaft' }, // Mapped to belongsToTeamId in backend
     { key: 'createdAt', label: 'Hinzugefügt' },
     { key: 'updatedAt', label: 'Zuletzt bearbeitet' }
  ], []);

  const handleAdvancedStateChange = (newState: AdvancedState) => {
    setSettings(prev => ({ ...prev, advancedState: newState }));
  };

  /* Removed handleModeChange as we are only using advanced now */

  const handleAdvancedReset = () => {
      setSettings(prev => ({ ...prev, advancedState: { filters: [], sorts: [{ key: 'firstName', direction: 'asc' as const }] } }));
      setSearchTerm("");
  };

  return (
    <div className="h-full flex flex-col">
       <PageHeader>
           <PageTitle 
             title="Spieler" 
             icon={Contact} 
             iconColor="text-blue-600"
           />
       </PageHeader>

      <DataView 
        viewMode={viewMode} 
        onViewModeChange={setViewMode}
        className="flex-1 bg-transparent"
        isLoading={isInitialLoading}
        isEmpty={allPlayers.length === 0}
        selectedIds={selectedIds}
        onMultiSelect={setSelectedIds}
        onSelectionDelete={handleSelectionDelete}
      >
        <div className="flex flex-col h-full">
          <DataViewToolbar
            searchQuery={searchTerm}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Spieler suchen..."
            infoText={`${data?.players?.count || 0} Spieler`}

            // Advanced Props (Always Advanced)
            mode="advanced"
            advancedFilterDefs={advancedFilterDefs}
            advancedSortDefs={advancedSortDefs}
            advancedState={advancedState}
            onAdvancedStateChange={handleAdvancedStateChange}
            onAdvancedReset={handleAdvancedReset}
            defaultSort={{ key: 'firstName', direction: 'asc' as SortDirection }}

            leading={
              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <PrimaryActionDropdown
                    triggerIcon={Plus}
                    triggerLabel="Spieler hinzufügen"
                    actions={[
                    {
                        label: "Spieler erstellen",
                        icon: Plus,
                        onClick: () => setCreatePlayerDialogOpen(true)
                    },
                    {
                        label: "CSV importieren",
                        icon: FileSpreadsheet,
                        onClick: () => setIsImportOpen(true)
                    }
                    ]}
                />
              </PermissionFeatureWrapper>
            }
          />

          <DataViewLayout>
            <DataViewContent
              items={genericItems}
              renderGridItem={(item) => (
                <DataViewGridCard
                  item={item}
                  actions={genericActions}
                />
              )}
              renderListItem={(item) => (
                <DataViewListCard
                  item={item}
                  actions={genericActions}
                />
              )}
              loadingIndicator={<DataViewLoadingState />}
              emptyState={
                <DataViewEmptyState
                  icon={<Contact className="h-8 w-8 text-gray-400" />}
                  title={searchTerm ? 'Keine Spieler gefunden' : 'Keine Spieler'}
                  description={searchTerm 
                    ? 'Versuchen Sie andere Suchbegriffe.'
                    : 'Erstellen Sie Ihren ersten Spieler oder importieren Sie Spieler per CSV.'
                  }
                  action={
                    !searchTerm ? (
                      <div className="flex flex-col items-center gap-2">
                        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                            <PrimaryActionButton
                            onClick={() => setCreatePlayerDialogOpen(true)}
                            label="Ersten Spieler erstellen"
                            icon={Plus}
                            />
                        </PermissionFeatureWrapper>
                        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                            <button
                            type="button"
                            onClick={() => setIsImportOpen(true)}
                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                            >
                            CSV importieren
                            </button>
                        </PermissionFeatureWrapper>
                      </div>
                    ) : undefined
                  }
                />
              }
            />
            
            <DataViewLoadMore
               onLoadMore={handleLoadMore}
               isLoadingMore={isLoadingMore}
               hasMore={hasMore}
            />
          </DataViewLayout>
        </div>

        <CsvImportModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          config={importConfig}
        />

        {/* Dialogs */}
        {detailsPopupOpen.open && detailsPopupOpen.user && (
          <PlayerDetailsDialog
            open={detailsPopupOpen.open}
            playerId={detailsPopupOpen.user._id}
            mode={detailsPopupOpen.mode}
            onClose={handleDetailsClose}
          />
        )}

        {createPlayerDialogOpen && (
          <CreatePlayerDialog
            open={createPlayerDialogOpen}
            onClose={(refetchData) => {
              setCreatePlayerDialogOpen(false);
              if (refetchData) {
                handleCreateSuccess();
              }
            }}
          />
        )}
        
        <StandardDeleteDialog 
          open={deleteConfirm.open} 
          onOpenChange={(val) => setDeleteConfirm(prev => ({ ...prev, open: val }))}
          onDelete={handleDelete}
          title={deleteConfirm.isBulk ? `${selectedIds.length} Spieler löschen` : "Spieler löschen"}
          description={deleteConfirm.isBulk ? "Möchten Sie diese Spieler wirklich unwiderruflich löschen?" : "Diese Aktion kann nicht rückgängig gemacht werden"}
          itemTitle={deleteConfirm.player ? `${deleteConfirm.player.firstName} ${deleteConfirm.player.lastName}` : undefined}
          itemTypeLabel={deleteConfirm.player ? "ID" : undefined}
          itemTypeValue={deleteConfirm.player?._id}
          itemsList={deleteConfirm.isBulk ? selectedIds.map(id => {
            const p = allPlayers.find(p => p._id === id);
            return {
              title: p ? `${p.firstName} ${p.lastName}` : id,
              subtitle: translatePosition(p?.position) || undefined
            };
          }) : undefined}
          isLoading={isDeleting || isBulkDeleting}
        />
      </DataView>
    </div>
  );
};

export default PlayersWrapper;
